/**
 * EveryINC - User Management UI Module
 * User management table and stats utilities
 */

import type { User, UserStats, UserRole } from './types';

function computeUserStats(users: User[]): UserStats {
  const active = users.filter(u => u.is_active).length;
  const inactive = users.length - active;
  const devs = users.filter(u => u.role === 'developer' && u.is_active).length;
  const activeDevs = users.filter(u => u.role === 'developer').length;
  const neverLogged = users.filter(u => !u.last_login).length;

  return {
    active,
    inactive,
    devs,
    activeDevs,
    neverLogged
  };
}

function buildEmptyUserTableMarkup(): string {
  return `
    <tr class="empty-state">
      <td colspan="7">
        <div class="empty-content">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" opacity="0.3">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          <div class="empty-title">Nenhum usuario encontrado</div>
          <div class="empty-desc">Os usuarios cadastrados aparecerao aqui.</div>
        </div>
      </td>
    </tr>
  `;
}

interface BuildUserTableOptions {
  getRoleLabel: (role: UserRole | string) => string;
  onEditName?: string;
  onToggleName?: string;
}

function buildUserTableMarkup(users: User[], options: BuildUserTableOptions): string {
  const {
    getRoleLabel,
    onEditName = 'editUser',
    onToggleName = 'toggleUserStatus'
  } = options;

  return users.map(user => {
    const initials = `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase();
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    const lastLogin = user.last_login
      ? new Date(user.last_login).toLocaleDateString('pt-BR')
      : 'Nunca';

    return `
      <tr>
        <td>
          <div style="display: flex; align-items: center; gap: 10px;">
            <div class="user-avatar-table">${initials}</div>
            <span>${fullName}</span>
          </div>
        </td>
        <td>${user.email}</td>
        <td>
          <span class="role-badge ${user.role}">${getRoleLabel(user.role)}</span>
        </td>
        <td>${user.company_name || '\u2014'}</td>
        <td>
          <span class="user-status ${user.is_active ? 'active' : 'inactive'}">
            <span class="user-status-dot"></span>
            ${user.is_active ? 'Ativo' : 'Inativo'}
          </span>
        </td>
        <td>${lastLogin}</td>
        <td>
          <div class="table-actions">
            <button class="action-btn" onclick="${onEditName}(${user.id})" title="Editar">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 20h9"/>
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
            </button>
            <button class="action-btn danger" onclick="${onToggleName}(${user.id}, ${!user.is_active})"
                    title="${user.is_active ? 'Desativar' : 'Ativar'}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                ${user.is_active
                  ? '<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="18" y1="8" x2="23" y2="13"/><line x1="23" y1="8" x2="18" y2="13"/>'
                  : '<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6"/><path d="M23 11h-6"/>'
                }
              </svg>
            </button>
          </div>
        </td>
      </tr>
    `;
  }).join('');
}

const EveryIncUserManagementUi = {
  computeUserStats,
  buildEmptyUserTableMarkup,
  buildUserTableMarkup
};

// Attach to window for IIFE compatibility
(window as Window).EveryIncUserManagementUi = EveryIncUserManagementUi;

export {
  computeUserStats,
  buildEmptyUserTableMarkup,
  buildUserTableMarkup
};

export default EveryIncUserManagementUi;
